import { createContext } from "react";
import type { AppId } from '@/desktop/DesktopContext';
import type { NsiteBookmark } from '@/desktop/apps/NsiteBrowserApp';

export type Theme = "dark" | "light" | "system";

export interface RelayMetadata {
  /** List of relays with read/write permissions */
  relays: { url: string; read: boolean; write: boolean }[];
  /** Unix timestamp of when the relay list was last updated */
  updatedAt: number;
}

/** Blossom server list metadata (BUD-03 / kind 10063), mirroring RelayMetadata for parity. */
export interface BlossomServerMetadata {
  /** Ordered list of Blossom server URLs (most trusted/reliable first per BUD-03). */
  servers: string[];
  /** Unix timestamp of when the server list was last updated (from kind 10063 created_at). */
  updatedAt: number;
}

export interface AppConfig {
  /** Current theme */
  theme: Theme;
  /** NIP-65 relay list metadata */
  relayMetadata: RelayMetadata;
  /** User's kind 10063 Blossom server list. */
  blossomServerMetadata: BlossomServerMetadata;
  /**
   * When true, the app's default Blossom servers are merged with the user's
   * servers (app servers first, deduped). When false, only the user's servers
   * are used.
   */
  useAppBlossomServers: boolean;
  /**
   * When true, the nostrVM app relays (relay.ditto.pub, relay.primal.net,
   * relay.damus.io) are used instead of the user's synced NIP-65 relay list.
   * Useful when your personal relays don't carry the content you want.
   */
  useAppRelays: boolean;
  /** Which apps are shown in the dock (customizable by user) */
  dockApps: AppId[];
  /** Nsite bookmarks that are pinned as desktop shortcuts */
  desktopShortcuts: NsiteBookmark[];
  /**
   * When true, a NIP-89 client tag is attached to every event published by
   * the user, linking back to the nostrVM app handler event.
   */
  attachClientTag: boolean;
}

export interface AppContextType {
  /** Current application configuration */
  config: AppConfig;
  /** Update configuration using a callback that receives current config and returns new config */
  updateConfig: (updater: (currentConfig: Partial<AppConfig>) => Partial<AppConfig>) => void;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);
